import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { MessageSquare, GraduationCap, Building2, Rocket, Store, Calendar, Check, HelpCircle, Star, Clock, FileText, Send, X, ArrowLeft, Phone, BarChart3 } from "lucide-react";
import { useGuidedTour, TourButton } from "../components/GuidedTour";
import HalaAvatar, { AVATAR_STATES } from "../components/HalaAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global Font
const FONT_FAMILY = "'Tajawal', 'Segoe UI', sans-serif";

// SDB Brand Colors - Balanced
const COLORS = {
  primary: '#1D8F4E',      // الأخضر الرئيسي - للأزرار فقط
  primaryLight: '#E8F5EE', // الأخضر الفاتح - قليل جداً
  primaryDark: '#167A42',  // أخضر غامق للـ hover
  secondary: '#1B3A6B',    // الأزرق الداكن (للنصوص والـ headers)
  gold: '#C5A572',         // ذهبي (accent للعناصر المميزة)
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgLight: '#FAFAFA',
  bgGray: '#F1F5F9',       // رمادي فاتح للخلفيات
  border: '#E2E8F0',
  borderLight: '#F1F5F9',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
  // Hala bubble - أخضر مميز
  halaBubble: '#1D8F4E',
  halaBubbleLight: '#E8F5EE',
};

const SERVICE_CATEGORIES = [
  { icon: MessageSquare, label: "استشارات متخصصة" },
  { icon: GraduationCap, label: "برامج تدريبية" },
  { icon: Building2, label: "مساحات عمل" },
  { icon: Rocket, label: "برامج تطوير" },
  { icon: Store, label: "قنوات بيع" },
  { icon: Calendar, label: "فعاليات وورش عمل" },
];

// Category Info
const CATEGORY_INFO = {
  advisory: { name: 'الاستشارات', icon: MessageSquare, color: '#3B82F6' },
  training: { name: 'التدريب', icon: GraduationCap, color: '#8B5CF6' },
  workspaces: { name: 'مساحات العمل', icon: Building2, color: '#F59E0B' },
  sales_channels: { name: 'قنوات البيع', icon: Store, color: '#EC4899' },
  programs: { name: 'البرامج التطويرية', icon: Rocket, color: '#10B981' },
};

function TypingIndicator() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', fontFamily: FONT_FAMILY }}>
      <HalaAvatar state={AVATAR_STATES.THINKING} size={40} showStatusDot={false} />
      <div style={{ backgroundColor: COLORS.halaBubble, padding: '12px 16px', borderRadius: '16px', borderTopRightRadius: '4px' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <span style={{ width: '8px', height: '8px', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '50%', animation: 'bounce 1s infinite' }}></span>
          <span style={{ width: '8px', height: '8px', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '50%', animation: 'bounce 1s infinite', animationDelay: '150ms' }}></span>
          <span style={{ width: '8px', height: '8px', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '50%', animation: 'bounce 1s infinite', animationDelay: '300ms' }}></span>
        </div>
      </div>
    </div>
  );
}

function QuickReplies({ replies, onSelect }) {
  if (!replies || replies.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', fontFamily: FONT_FAMILY }}>
      {replies.map((reply, i) => (
        <button key={i} onClick={() => onSelect(reply)} style={{ padding: '10px 16px', backgroundColor: 'white', border: `1px solid ${COLORS.border}`, borderRadius: '9999px', fontSize: '14px', color: COLORS.secondary, fontWeight: 500, cursor: 'pointer', fontFamily: FONT_FAMILY, transition: 'all 0.2s' }}>
          {reply}
        </button>
      ))}
    </div>
  );
}

function ServiceSummaryCard({ summary }) {
  if (!summary) return null;
  return (
    <div style={{ marginTop: '12px', padding: '16px', background: 'white', borderRadius: '16px', border: `2px solid ${COLORS.primary}`, fontFamily: FONT_FAMILY }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: COLORS.primary, marginBottom: '12px' }}>
        <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check style={{ width: '14px', height: '14px', color: 'white' }} /></div>
        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>تم تقديم طلبك بنجاح!</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.textMuted }}>رقم الطلب:</span><span style={{ fontFamily: 'monospace', color: COLORS.secondary, fontWeight: 500 }}>{summary.request_id}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.textMuted }}>الخدمة:</span><span style={{ color: COLORS.secondary }}>{summary.service_name}</span></div>
      </div>
      
      {/* Divider */}
      <div style={{ height: '1px', backgroundColor: COLORS.border, margin: '12px 0' }} />
      
      {/* SMS Notification */}
      {summary.sms_sent && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: COLORS.primary, marginBottom: '8px' }}>
          <Phone style={{ width: '14px', height: '14px' }} />
          <span>تم إرسال تفاصيل الخدمة عبر SMS لرقم الجوال ({summary.sms_mobile})</span>
        </div>
      )}
      
      {/* SLA - Contact within 24h */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: COLORS.gold, fontWeight: 500 }}>
        <Clock style={{ width: '14px', height: '14px' }} />
        <span>سيتم التواصل معكم خلال 24 ساعة بإذن الله</span>
      </div>
    </div>
  );
}

// ─── Inline Plan Card (shown inside chat messages) ────────────────────────────
const PROGRAM_ICONS_MAP = {
  briefcase: FileText,
  building: Building2,
  graduation: GraduationCap,
  store: Store,
  rocket: Rocket,
};

function InlinePlanCard({ planData, onStartService, completedServices = [], inProgressServices = [], activeServiceId }) {
  if (!planData?.services?.length) return null;
  const { services, diagnosis } = planData;

  // Group by program
  const groups = {};
  services.forEach(svc => {
    const pid = svc.program_id || 'other';
    if (!groups[pid]) {
      groups[pid] = {
        name: svc.program_name_ar || 'خدمات أخرى',
        color: svc.program_color || '#64748B',
        icon: svc.program_icon || '',
        items: [],
      };
    }
    groups[pid].items.push(svc);
  });

  const getStatus = (svc) => {
    if (completedServices.includes(svc.service_id)) return 'completed';
    if (inProgressServices.includes(svc.service_id) || activeServiceId === svc.service_id) return 'in_progress';
    return 'not_started';
  };

  return (
    <div style={{ marginTop: '12px', borderRadius: '20px', overflow: 'hidden', border: `1px solid ${COLORS.border}`, backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${COLORS.secondary}, #243B6A)`, padding: '18px 20px', color: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: 0, fontFamily: FONT_FAMILY }}>خطة الدعم الخاصة بك</h3>
          <span style={{ fontSize: '11px', backgroundColor: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontFamily: FONT_FAMILY }}>
            {services.length} خدمات
          </span>
        </div>
        {diagnosis?.city && (
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontFamily: FONT_FAMILY, display: 'block', marginTop: '6px' }}>
            {diagnosis.city} • {diagnosis.support_type || diagnosis.main_problem || ''}
          </span>
        )}
      </div>

      {/* Programs & Services */}
      <div style={{ padding: '16px' }}>
        {Object.entries(groups).map(([pid, group]) => {
          const IconComp = PROGRAM_ICONS_MAP[group.icon] || Star;
          return (
            <div key={pid} style={{ marginBottom: '16px' }}>
              {/* Program Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', padding: '8px 12px', backgroundColor: `${group.color}10`, borderRadius: '10px', borderRight: `3px solid ${group.color}` }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: group.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
                  <IconComp style={{ width: '16px', height: '16px' }} />
                </div>
                <div>
                  <h4 style={{ fontSize: '13px', fontWeight: 'bold', color: COLORS.secondary, margin: 0, fontFamily: FONT_FAMILY }}>خدمات {group.name}</h4>
                  <span style={{ fontSize: '10px', color: COLORS.textMuted, fontFamily: FONT_FAMILY }}>{group.items.length} {group.items.length === 1 ? 'خدمة' : 'خدمات'}</span>
                </div>
              </div>

              {/* Service Cards */}
              {group.items.map(svc => {
                const status = getStatus(svc);
                const isActive = activeServiceId === svc.service_id;
                return (
                  <div key={svc.service_id} style={{
                    padding: '12px 14px', marginBottom: '8px', marginRight: '8px',
                    borderRadius: '12px',
                    backgroundColor: status === 'completed' ? '#F0FDF4' : isActive ? '#FEFCE8' : 'white',
                    border: `1px solid ${status === 'completed' ? COLORS.primary : isActive ? '#F59E0B' : COLORS.border}`,
                    transition: 'all 0.2s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <h5 style={{ fontSize: '13px', fontWeight: 'bold', color: COLORS.secondary, margin: '0 0 4px', fontFamily: FONT_FAMILY }}>
                          {svc.service_display_name || svc.service_name_ar}
                        </h5>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          {svc.delivery_mode_label && (
                            <span style={{
                              fontSize: '10px', fontWeight: 500,
                              color: svc.delivery_mode === 'online' ? '#3B82F6' : svc.delivery_mode === 'in_person' ? '#D97706' : '#7C3AED',
                              backgroundColor: svc.delivery_mode === 'online' ? '#EFF6FF' : svc.delivery_mode === 'in_person' ? '#FFFBEB' : '#F5F3FF',
                              padding: '2px 8px', borderRadius: '10px', fontFamily: FONT_FAMILY,
                            }}>
                              {svc.delivery_mode_label}
                            </span>
                          )}
                          {status === 'completed' && (
                            <span style={{ fontSize: '10px', fontWeight: 600, color: COLORS.primary, backgroundColor: '#DCFCE7', padding: '2px 8px', borderRadius: '10px', fontFamily: FONT_FAMILY, display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <Check style={{ width: '10px', height: '10px' }} /> تم التقديم
                            </span>
                          )}
                          {isActive && (
                            <span style={{ fontSize: '10px', fontWeight: 600, color: '#D97706', backgroundColor: '#FEF3C7', padding: '2px 8px', borderRadius: '10px', fontFamily: FONT_FAMILY, display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <Rocket style={{ width: '10px', height: '10px' }} /> جاري التقديم
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action Button */}
                      {status === 'not_started' && !isActive && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onStartService(svc.service_id); }}
                          data-testid={`start-service-${svc.service_id}`}
                          style={{
                            padding: '6px 16px', backgroundColor: group.color, color: 'white',
                            border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold',
                            cursor: 'pointer', fontFamily: FONT_FAMILY, whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          ابدأ
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConsultantCard({ consultant, onSelect, isRecommended }) {
  return (
    <div onClick={() => onSelect(consultant)} style={{ padding: '16px', backgroundColor: 'white', borderRadius: '16px', border: isRecommended ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.border}`, cursor: 'pointer', boxShadow: isRecommended ? '0 4px 6px -1px rgba(197, 165, 114, 0.2)' : 'none', fontFamily: FONT_FAMILY }}>
      {isRecommended && <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: COLORS.gold, fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}><Star style={{ width: '12px', height: '12px', fill: 'currentColor' }} /><span>أفضل تطابق</span></div>}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <img src={consultant.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(consultant.name)}&background=1B3A6B&color=fff`} alt={consultant.name} style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', border: `2px solid ${COLORS.border}` }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 style={{ fontWeight: 'bold', color: COLORS.secondary, fontSize: '14px', margin: 0, fontFamily: FONT_FAMILY }}>{consultant.name}</h4>
          <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '4px 0 0 0', fontFamily: FONT_FAMILY }}>{consultant.title}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px', fontSize: '10px', color: COLORS.textLight }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Star style={{ width: '12px', height: '12px', color: '#fbbf24', fill: 'currentColor' }} />{consultant.rating}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Clock style={{ width: '12px', height: '12px' }} />{consultant.experience_years} سنة</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PLAN JOURNEY DASHBOARD - تجربة مسار متكاملة
// ═══════════════════════════════════════════════════════════════════════════════

// Service Status Constants
const SERVICE_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  REQUEST_SENT: 'REQUEST_SENT',
};

const STATUS_CONFIG = {
  NOT_STARTED: { label: 'لم يبدأ', color: COLORS.textLight, bgColor: COLORS.bgGray, icon: Clock },
  IN_PROGRESS: { label: 'قيد التنفيذ', color: COLORS.gold, bgColor: '#FEF3C7', icon: Rocket },
  REQUEST_SENT: { label: 'مكتمل', color: COLORS.primary, bgColor: COLORS.primaryLight, icon: Check },
};

function PlanJourneyDashboard({ 
  services, 
  diagnosis, 
  onSelectService, 
  activeServiceId,
  completedServices = [],
  inProgressServices = []
}) {
  // Calculate progress
  const totalServices = services.length;
  const completedCount = completedServices.length;
  const inProgressCount = inProgressServices.length;
  const progressPercent = totalServices > 0 ? Math.round((completedCount / totalServices) * 100) : 0;

  // Get service status
  const getServiceStatus = (serviceId) => {
    if (completedServices.includes(serviceId)) return SERVICE_STATUS.REQUEST_SENT;
    if (inProgressServices.includes(serviceId) || activeServiceId === serviceId) return SERVICE_STATUS.IN_PROGRESS;
    return SERVICE_STATUS.NOT_STARTED;
  };

  // Find next recommended service
  const nextService = services.find(s => 
    !completedServices.includes(s.service_id) && 
    !inProgressServices.includes(s.service_id) &&
    s.service_id !== activeServiceId
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.bgGray, fontFamily: FONT_FAMILY }}>
      {/* Header with Progress */}
      <div style={{ backgroundColor: COLORS.secondary, padding: '20px 16px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Rocket style={{ width: '20px', height: '20px', color: 'white' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: 'white', margin: 0, fontFamily: FONT_FAMILY }}>خطة الدعم الخاصة بك</h2>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', margin: '2px 0 0 0', fontFamily: FONT_FAMILY }}>
              {completedCount} من {totalServices} خدمات مكتملة
            </p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '10px', height: '8px', overflow: 'hidden' }}>
          <div style={{ 
            width: `${progressPercent}%`, 
            height: '100%', 
            backgroundColor: COLORS.primary,
            borderRadius: '10px',
            transition: 'width 0.5s ease'
          }}></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_FAMILY }}>
            التقدم: {progressPercent}%
          </span>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_FAMILY }}>
            {inProgressCount > 0 ? `${inProgressCount} قيد التنفيذ` : ''}
          </span>
        </div>
      </div>

      {/* Diagnosis Summary - Compact */}
      <div style={{ backgroundColor: 'white', padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {[
            { label: 'المشروع', value: diagnosis?.project_stage },
            { label: 'التحدي', value: diagnosis?.main_problem },
            { label: 'المدينة', value: diagnosis?.city },
          ].filter(item => item.value).map(item => (
            <span key={item.label} style={{ 
              fontSize: '11px', 
              color: COLORS.textMuted, 
              backgroundColor: COLORS.bgGray, 
              padding: '4px 10px', 
              borderRadius: '20px',
              fontFamily: FONT_FAMILY 
            }}>
              {item.label}: <strong style={{ color: COLORS.secondary }}>{item.value}</strong>
            </span>
          ))}
        </div>
      </div>

      {/* Services Grouped by Program */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {(() => {
          // Group services by program
          const programGroups = {};
          services.forEach(service => {
            const pid = service.program_id || 'other';
            if (!programGroups[pid]) {
              programGroups[pid] = {
                program_id: pid,
                program_name_ar: service.program_name_ar || 'خدمات أخرى',
                program_color: service.program_color || '#64748B',
                program_icon: service.program_icon || '',
                services: [],
              };
            }
            programGroups[pid].services.push(service);
          });

          const PROGRAM_ICONS = {
            briefcase: <FileText style={{ width: '16px', height: '16px' }} />,
            building: <Building2 style={{ width: '16px', height: '16px' }} />,
            graduation: <GraduationCap style={{ width: '16px', height: '16px' }} />,
            store: <Store style={{ width: '16px', height: '16px' }} />,
            rocket: <Rocket style={{ width: '16px', height: '16px' }} />,
          };

          return Object.values(programGroups).map((group) => (
            <div key={group.program_id} style={{ marginBottom: '16px' }}>
              {/* Program Header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: '10px', padding: '8px 12px',
                backgroundColor: `${group.program_color}12`,
                borderRadius: '10px',
                borderRight: `3px solid ${group.program_color}`,
              }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '8px',
                  backgroundColor: group.program_color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', flexShrink: 0,
                }}>
                  {PROGRAM_ICONS[group.program_icon] || <Star style={{ width: '16px', height: '16px' }} />}
                </div>
                <div>
                  <h3 style={{ fontSize: '13px', fontWeight: 'bold', color: COLORS.secondary, margin: 0, fontFamily: FONT_FAMILY }}>
                    خدمات {group.program_name_ar}
                  </h3>
                  <span style={{ fontSize: '10px', color: COLORS.textMuted, fontFamily: FONT_FAMILY }}>
                    {group.services.length} {group.services.length === 1 ? 'خدمة' : 'خدمات'}
                  </span>
                </div>
              </div>

              {/* Services in this program */}
              {group.services.map((service) => {
                const status = getServiceStatus(service.service_id);
                const statusConfig = STATUS_CONFIG[status];
                const StatusIcon = statusConfig.icon;
                const isActive = service.service_id === activeServiceId;
                const isNext = nextService?.service_id === service.service_id;

                return (
                  <div
                    key={service.service_id}
                    onClick={() => status !== SERVICE_STATUS.REQUEST_SENT && onSelectService(service.service_id)}
                    style={{
                      backgroundColor: isActive ? 'white' : status === SERVICE_STATUS.REQUEST_SENT ? COLORS.primaryLight : 'white',
                      borderRadius: '12px',
                      padding: '12px 14px',
                      border: isActive ? `2px solid ${group.program_color}` : `1px solid ${status === SERVICE_STATUS.REQUEST_SENT ? COLORS.primary : COLORS.border}`,
                      cursor: status === SERVICE_STATUS.REQUEST_SENT ? 'default' : 'pointer',
                      marginBottom: '8px',
                      marginRight: '8px',
                      boxShadow: isActive ? `0 4px 12px ${group.program_color}25` : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {/* Top row: status + next */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{
                        fontSize: '10px', fontWeight: 600,
                        color: statusConfig.color, backgroundColor: statusConfig.bgColor,
                        padding: '3px 10px', borderRadius: '20px', fontFamily: FONT_FAMILY,
                        display: 'flex', alignItems: 'center', gap: '4px',
                      }}>
                        <StatusIcon style={{ width: '12px', height: '12px' }} />
                        {statusConfig.label}
                      </span>
                      {isNext && status === SERVICE_STATUS.NOT_STARTED && (
                        <span style={{ fontSize: '10px', fontWeight: 600, color: group.program_color, fontFamily: FONT_FAMILY }}>← ابدأ</span>
                      )}
                    </div>

                    {/* Service Name */}
                    <h4 style={{ fontSize: '13px', fontWeight: 'bold', color: COLORS.secondary, margin: '0 0 4px 0', fontFamily: FONT_FAMILY }}>
                      {service.service_display_name || service.service_name_ar}
                    </h4>

                    {/* Delivery Mode */}
                    {service.delivery_mode_label && (
                      <span style={{
                        fontSize: '10px', fontWeight: 500,
                        color: service.delivery_mode === 'online' ? '#3B82F6' : service.delivery_mode === 'in_person' ? '#F59E0B' : '#8B5CF6',
                        backgroundColor: service.delivery_mode === 'online' ? '#EFF6FF' : service.delivery_mode === 'in_person' ? '#FFFBEB' : '#F5F3FF',
                        padding: '2px 8px', borderRadius: '10px', fontFamily: FONT_FAMILY,
                        display: 'inline-block', marginBottom: '4px',
                      }}>
                        {service.delivery_mode_label}
                      </span>
                    )}

                    {/* Description */}
                    <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: 0, lineHeight: 1.4, fontFamily: FONT_FAMILY }}>
                      {service.short_description_ar || service.service_description_ar || 'خدمة دعم متخصصة'}
                    </p>

                    {/* Active hint */}
                    {isActive && (
                      <div style={{ marginTop: '8px', padding: '6px 10px', backgroundColor: group.program_color, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ArrowLeft style={{ width: '14px', height: '14px', color: 'white' }} />
                        <span style={{ fontSize: '11px', color: 'white', fontFamily: FONT_FAMILY }}>أكمل الخطوات في المحادثة</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ));
        })()}

        {/* All Complete Message */}
        {completedCount === totalServices && totalServices > 0 && (
          <div style={{ 
            marginTop: '20px',
            padding: '16px',
            backgroundColor: COLORS.primaryLight,
            borderRadius: '14px',
            textAlign: 'center',
            border: `1px solid ${COLORS.primary}`
          }}>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              borderRadius: '50%', 
              backgroundColor: COLORS.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px'
            }}>
              <Star style={{ width: '24px', height: '24px', color: 'white', fill: 'white' }} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: COLORS.primary, margin: '0 0 4px 0', fontFamily: FONT_FAMILY }}>
              أحسنت! أكملت جميع الخدمات
            </h3>
            <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: 0, fontFamily: FONT_FAMILY }}>
              سيتم التواصل معك قريباً
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 16px', borderTop: `1px solid ${COLORS.border}`, backgroundColor: 'white', flexShrink: 0 }}>
        <p style={{ fontSize: '11px', color: COLORS.textLight, textAlign: 'center', margin: 0, fontFamily: FONT_FAMILY }}>
          اختر خدمة للبدء أو أكمل الخطوات في المحادثة
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// GLOWING PLAN BUTTON - ابدأ خطتك
// ═══════════════════════════════════════════════════════════════════════════════

function GlowingPlanButton({ onClick, servicesCount }) {
  return (
    <div style={{ padding: '16px', borderTop: `1px solid ${COLORS.border}`, backgroundColor: 'white' }}>
      <button 
        onClick={onClick}
        data-testid="start-plan-btn"
        style={{ 
          width: '100%',
          padding: '16px 20px',
          backgroundColor: COLORS.primary,
          color: 'white',
          borderRadius: '16px',
          border: 'none',
          fontSize: '15px',
          fontWeight: 'bold',
          cursor: 'pointer',
          fontFamily: FONT_FAMILY,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          boxShadow: `0 0 20px ${COLORS.primary}60, 0 0 40px ${COLORS.primary}40, 0 0 60px ${COLORS.primary}20`,
          animation: 'glow-pulse 2s ease-in-out infinite',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Rocket style={{ width: '20px', height: '20px' }} />
        <span>ابدأ خطتك</span>
        <span style={{ 
          backgroundColor: 'rgba(255,255,255,0.25)', 
          padding: '2px 10px', 
          borderRadius: '20px', 
          fontSize: '12px',
          fontWeight: 600
        }}>
          {servicesCount} خدمات
        </span>
      </button>
      <p style={{ fontSize: '11px', color: COLORS.textMuted, textAlign: 'center', margin: '10px 0 0 0', fontFamily: FONT_FAMILY }}>
        خطتك جاهزة! اضغط للبدء في رحلة الدعم
      </p>
      <style>{`
        @keyframes glow-pulse {
          0%, 100% { 
            box-shadow: 0 0 20px ${COLORS.primary}60, 0 0 40px ${COLORS.primary}40, 0 0 60px ${COLORS.primary}20;
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 30px ${COLORS.primary}80, 0 0 60px ${COLORS.primary}50, 0 0 80px ${COLORS.primary}30;
            transform: scale(1.02);
          }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DYNAMIC DASHBOARD (for Discovery/Reflection states)
// ═══════════════════════════════════════════════════════════════════════════════

function DynamicDashboard({ 
  currentState, 
  dashboardData, 
  onStartService, 
  onStartDiscovery, 
  showPlanButton, 
  onShowPlan, 
  planServicesCount, 
  onStartTour,
  // New props for Plan Journey
  isPlanMode,
  planServices,
  planDiagnosis,
  activeServiceId,
  completedServices,
  inProgressServices
}) {
  
  const WelcomeDashboard = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.bgGray, fontFamily: FONT_FAMILY }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '24px' }}>
        <HalaAvatar state={AVATAR_STATES.IDLE} size={140} showStatusDot={false} style={{ marginBottom: '20px' }} />
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.secondary, marginBottom: '8px', fontFamily: FONT_FAMILY }}>مرحباً بك في هلا</h2>
        <p style={{ color: COLORS.textMuted, fontSize: '13px', marginBottom: '20px', maxWidth: '240px', lineHeight: 1.6, fontFamily: FONT_FAMILY }}>مساعدتك الذكية لاكتشاف الخدمات الغير المالية</p>
        <div data-tour="service-categories" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', width: '100%', maxWidth: '260px' }}>
          {SERVICE_CATEGORIES.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', backgroundColor: 'white', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: COLORS.bgGray, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><f.icon style={{ width: '14px', height: '14px', color: COLORS.secondary }} /></div>
              <span style={{ fontSize: '11px', fontWeight: 500, color: COLORS.secondary, fontFamily: FONT_FAMILY }}>{f.label}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Tour Button - Only in Welcome state */}
      <div style={{ padding: '16px', borderTop: `1px solid ${COLORS.border}`, backgroundColor: 'white' }}>
        <TourButton onClick={onStartTour} />
      </div>
    </div>
  );

  const DiscoveryDashboard = () => {
    const steps = [{ key: "project_stage", label: "مرحلة المشروع" }, { key: "main_problem", label: "التحدي الرئيسي" }, { key: "support_type", label: "نوع الدعم" }, { key: "sector", label: "مجال العمل" }, { key: "city", label: "المدينة" }];
    const currentStep = dashboardData?.current_step || 0;
    const collectedData = dashboardData?.collected_data || {};
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.bgGray, fontFamily: FONT_FAMILY }}>
        <div style={{ padding: '16px', borderBottom: `1px solid ${COLORS.border}`, backgroundColor: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '4px', height: '28px', backgroundColor: COLORS.secondary, borderRadius: '9999px' }}></div>
            <div><h2 style={{ fontSize: '16px', fontWeight: 'bold', color: COLORS.secondary, margin: 0, fontFamily: FONT_FAMILY }}>مرحلة التشخيص</h2><p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '2px 0 0 0', fontFamily: FONT_FAMILY }}>نجمع معلوماتك</p></div>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '12px', marginBottom: '20px', border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ fontWeight: 'bold', color: COLORS.secondary, fontSize: '14px', margin: '0 0 4px 0', fontFamily: FONT_FAMILY }}>رحلة فهم احتياجك</h3>
            <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: 0, fontFamily: FONT_FAMILY }}>نجمع معلوماتك لبناء خطة دعم مخصصة</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {steps.map((step, index) => {
              const isCompleted = index < currentStep, isCurrent = index === currentStep, answer = collectedData[step.key];
              return (
                <div key={step.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '13px', backgroundColor: isCompleted ? COLORS.primary : isCurrent ? COLORS.secondary : 'white', color: isCompleted || isCurrent ? 'white' : COLORS.textLight, fontFamily: FONT_FAMILY, border: isCompleted || isCurrent ? 'none' : `1px solid ${COLORS.border}` }}>
                      {isCompleted ? <Check style={{ width: '14px', height: '14px' }} /> : index + 1}
                    </div>
                    {index < steps.length - 1 && <div style={{ width: '2px', height: '32px', marginTop: '4px', backgroundColor: isCompleted ? COLORS.primary : COLORS.border }}></div>}
                  </div>
                  <div style={{ flex: 1, paddingTop: '4px' }}>
                    <h4 style={{ fontWeight: 'bold', fontSize: '13px', color: isCompleted || isCurrent ? COLORS.secondary : COLORS.textLight, margin: 0, fontFamily: FONT_FAMILY }}>{step.label}</h4>
                    {isCurrent && !answer && <span style={{ fontSize: '11px', color: COLORS.gold, fontWeight: 600, fontFamily: FONT_FAMILY }}>جارٍ الآن</span>}
                    {answer && <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '2px 0 0 0', fontFamily: FONT_FAMILY }}>{answer}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Glowing Plan Button */}
        {showPlanButton && <GlowingPlanButton onClick={onShowPlan} servicesCount={planServicesCount} />}
      </div>
    );
  };

  const ReflectionDashboard = () => {
    const summary = dashboardData?.diagnosis_summary || dashboardData?.collected_data || {};
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.bgGray, fontFamily: FONT_FAMILY }}>
        <div style={{ padding: '16px', borderBottom: `1px solid ${COLORS.border}`, backgroundColor: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '4px', height: '28px', backgroundColor: COLORS.gold, borderRadius: '9999px' }}></div>
            <div><h2 style={{ fontSize: '16px', fontWeight: 'bold', color: COLORS.secondary, margin: 0, fontFamily: FONT_FAMILY }}>تأكيد المعلومات</h2><p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '2px 0 0 0', fontFamily: FONT_FAMILY }}>راجع المعلومات</p></div>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: `1px solid ${COLORS.border}`, padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}><Check style={{ width: '16px', height: '16px', color: COLORS.primary }} /><h3 style={{ fontWeight: 'bold', color: COLORS.secondary, fontSize: '14px', margin: 0, fontFamily: FONT_FAMILY }}>ملخص التشخيص</h3></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '14px' }}>
              {[{ key: 'project_stage', label: 'المشروع' }, { key: 'main_problem', label: 'التحدي' }, { key: 'support_type', label: 'الدعم' }, { key: 'sector', label: 'المجال' }, { key: 'city', label: 'المدينة' }].map(item => (
                <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '6px', borderBottom: `1px solid ${COLORS.bgGray}` }}>
                  <span style={{ color: COLORS.textMuted, fontFamily: FONT_FAMILY }}>{item.label}:</span>
                  <span style={{ color: COLORS.secondary, fontWeight: 500, fontFamily: FONT_FAMILY }}>{summary[item.key] || "-"}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '12px', border: `1px solid ${COLORS.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: COLORS.secondary, marginBottom: '6px' }}><HelpCircle style={{ width: '16px', height: '16px' }} /><span style={{ fontWeight: 'bold', fontSize: '14px', fontFamily: FONT_FAMILY }}>هل المعلومات صحيحة؟</span></div>
            <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: 0, fontFamily: FONT_FAMILY }}>أكد المعلومات في المحادثة للمتابعة</p>
          </div>
        </div>
        {/* Glowing Plan Button */}
        {showPlanButton && <GlowingPlanButton onClick={onShowPlan} servicesCount={planServicesCount} />}
      </div>
    );
  };

  const renderContent = () => {
    // If in Plan Mode, show Plan Journey Dashboard
    if (isPlanMode && planServices?.length > 0) {
      return (
        <PlanJourneyDashboard 
          services={planServices}
          diagnosis={planDiagnosis}
          onSelectService={onStartService}
          activeServiceId={activeServiceId}
          completedServices={completedServices || []}
          inProgressServices={inProgressServices || []}
        />
      );
    }

    switch (currentState) {
      case "WELCOME": 
        return <WelcomeDashboard />;
      case "DISCOVERY": 
        return <DiscoveryDashboard />;
      case "REFLECTION":
      case "RESULT_READY":
      case "PLAN_READY":
      case "SERVICE_IN_PROGRESS":
      case "SERVICE_ACTIVE":
      case "CLIENT_INFO_CAPTURE":
      case "SERVICE_SUBMITTED":
        // All post-discovery states should show ReflectionDashboard (with Plan button if available)
        return <ReflectionDashboard />;
      default: 
        // For any unknown state after journey starts, show ReflectionDashboard
        // Only show WelcomeDashboard if we're truly at the beginning
        return <ReflectionDashboard />;
    }
  };
  return <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white' }}>{renderContent()}</div>;
}

function PlanReadyPopup({ title, subtitle, buttonText, onShowPlan }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px', fontFamily: FONT_FAMILY }}>
      <div style={{ backgroundColor: 'white', borderRadius: '24px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', padding: '32px', maxWidth: '350px', width: '100%', textAlign: 'center' }}>
        <div style={{ width: '80px', height: '80px', margin: '0 auto 20px', borderRadius: '50%', backgroundColor: COLORS.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Rocket style={{ width: '40px', height: '40px', color: 'white' }} /></div>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.secondary, marginBottom: '12px', fontFamily: FONT_FAMILY }}>{title || "خطة الدعم جاهزة"}</h2>
        <p style={{ color: COLORS.textMuted, fontSize: '14px', marginBottom: '24px', lineHeight: 1.6, fontFamily: FONT_FAMILY }}>{subtitle || "بناءً على المعلومات التي ذكرتها، جهزت لك هلا خطة دعم مبدئية."}</p>
        <button onClick={onShowPlan} style={{ width: '100%', padding: '16px', backgroundColor: COLORS.primary, color: 'white', borderRadius: '12px', fontWeight: 'bold', fontSize: '16px', border: 'none', cursor: 'pointer', fontFamily: FONT_FAMILY, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <Rocket style={{ width: '20px', height: '20px' }} />
          {buttonText || "ابدأ خطتك"}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN CHAT PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function ChatPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [currentState, setCurrentState] = useState("WELCOME");
  const [dashboardData, setDashboardData] = useState(null);
  const [showPopup, setShowPopup] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [isPlanMode, setIsPlanMode] = useState(false);
  const [planServices, setPlanServices] = useState([]);
  const [diagnosisSummary, setDiagnosisSummary] = useState({});
  const [completedServices, setCompletedServices] = useState([]);
  const [inProgressServices, setInProgressServices] = useState([]);
  const [activeServiceId, setActiveServiceId] = useState(null);
  const [showDashboard, setShowDashboard] = useState(window.innerWidth >= 768);
  const [avatarState, setAvatarState] = useState(AVATAR_STATES.IDLE);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [chatPaused, setChatPaused] = useState(false);
  const [pauseMessage, setPauseMessage] = useState("");
  const [allServicesCompleted, setAllServicesCompleted] = useState(false);
  const messagesEndRef = useRef(null);
  // Capture mobile_number from URL before any effects run
  const pendingMobileRef = useRef(searchParams.get('mobile_number'));

  const scrollToBottom = useCallback(() => { setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100); }, []);

  // Clean URL param immediately if present
  useEffect(() => {
    if (pendingMobileRef.current) {
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams]);

  useEffect(() => { createSession(); }, []);
  useEffect(() => { scrollToBottom(); }, [messages, isTyping, scrollToBottom]);
  useEffect(() => {
    const handleResize = () => setShowDashboard(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Avatar state management
  useEffect(() => {
    if (isTyping) {
      setAvatarState(AVATAR_STATES.THINKING);
    } else if (isUserTyping) {
      setAvatarState(AVATAR_STATES.LISTENING);
    } else if (currentState === 'PLAN_READY' || currentState === 'RESULT_READY' || showPopup) {
      setAvatarState(AVATAR_STATES.SUCCESS);
    } else if (messages.length > 0 && messages[messages.length - 1]?.sender === 'ai') {
      // After AI sends message, show speaking state briefly then return to idle
      setAvatarState(AVATAR_STATES.SPEAKING);
      const timer = setTimeout(() => setAvatarState(AVATAR_STATES.IDLE), 2000);
      return () => clearTimeout(timer);
    } else {
      setAvatarState(AVATAR_STATES.IDLE);
    }
  }, [isTyping, isUserTyping, currentState, messages, showPopup]);

  // Track user typing
  useEffect(() => {
    if (inputText.trim()) {
      setIsUserTyping(true);
    } else {
      const timer = setTimeout(() => setIsUserTyping(false), 500);
      return () => clearTimeout(timer);
    }
  }, [inputText]);

  const createSession = async () => {
    try {
      const res = await axios.post(`${API}/sessions`);
      const { session_id, current_state, dashboard_data } = res.data;
      setSessionId(session_id); setCurrentState(current_state); setDashboardData(dashboard_data);
      setMessages([]); setConsultants([]); setQuickReplies([]); setShowPopup(null);
      setIsPlanMode(false); setPlanServices([]); setDiagnosisSummary({}); 
      setCompletedServices([]); setInProgressServices([]); setActiveServiceId(null);
      setChatPaused(false); setPauseMessage(""); setAllServicesCompleted(false);

      // Auto-load plan if navigated from "Review Plan" page
      const mobileNumber = pendingMobileRef.current;
      if (mobileNumber) {
        pendingMobileRef.current = null;
        try {
          const planRes = await axios.post(`${API}/plans/retrieve`, { mobile_number: mobileNumber });
          if (planRes.data.success && planRes.data.plan) {
            const plan = planRes.data.plan;
            // Use the ORIGINAL session_id from the plan (not the new one)
            if (plan.session_id) {
              setSessionId(plan.session_id);
              // Restore the session state
              try {
                const sessRes = await axios.get(`${API}/sessions/${plan.session_id}/state`);
                if (sessRes.data) {
                  setCurrentState(sessRes.data.current_state || 'PLAN_READY');
                  if (sessRes.data.dashboard_data) setDashboardData(sessRes.data.dashboard_data);
                  setActiveServiceId(sessRes.data.active_service_id || null);
                }
              } catch (e) {
                setCurrentState('PLAN_READY');
              }
            }
            setPlanServices(plan.services || []);
            setDiagnosisSummary(plan.diagnosis || {});
            setIsPlanMode(true);
            setChatPaused(true);
            setPauseMessage("أنت تراجع خطتك الحالية. اختر خدمة لمتابعة رحلتك.");
            setCompletedServices((plan.services || []).filter(s => s.status === 'COMPLETED').map(s => s.service_id));
            setInProgressServices((plan.services || []).filter(s => s.status === 'IN_PROGRESS').map(s => s.service_id));
            setMessages([{ sender: 'ai', text: `مرحباً ${plan.client_name || ''}! تم تحميل خطتك. يمكنك مراجعة خدماتك واختيار إحداها لمتابعة رحلتك.` }]);
          }
        } catch (err) {
          console.error('Auto-load plan error:', err);
        }
      }
    } catch (e) { console.error("Session init error:", e); }
  };

  const addMsg = (sender, text, extras = {}) => {
    const msg = { id: Date.now() + Math.random(), sender, text, time: new Date().toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" }), ...extras };
    setMessages((prev) => [...prev, msg]);
    return msg;
  };

  // Add plan card as an interactive message in the chat
  const addPlanCardMessage = (services, diagnosis) => {
    const msg = {
      id: Date.now() + Math.random(),
      sender: "ai",
      text: null,
      time: new Date().toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" }),
      plan_card: { services, diagnosis },
    };
    setMessages((prev) => [...prev, msg]);
  };

  const handleStartDiscovery = async () => {
    if (!sessionId) return;
    setIsTyping(true);
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/start`);
      const { message, ai_message, quick_replies, current_state, dashboard_data } = res.data;
      if (message || ai_message) addMsg("ai", message || ai_message);
      setQuickReplies(quick_replies || []); setCurrentState(current_state); setDashboardData(dashboard_data);
    } catch (e) { addMsg("ai", "عذراً، حدث خطأ. حاول مرة أخرى."); }
    finally { setIsTyping(false); }
  };

  const handleSend = async (text) => {
    const trimmed = text?.trim();
    if (!trimmed || !sessionId) return;
    addMsg("user", trimmed); setInputText(""); setQuickReplies([]); setIsTyping(true);
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/chat`, { message: trimmed });
      const { message, ai_message, quick_replies, current_state, dashboard_data, show_popup, consultants: newConsultants, service_summary, chat_paused, pause_message, all_services_completed, service_completed } = res.data;
      if (message || ai_message) addMsg("ai", message || ai_message, { service_summary });
      setQuickReplies(quick_replies || []); setCurrentState(current_state); setDashboardData(dashboard_data);
      if (newConsultants) setConsultants(newConsultants);
      if (show_popup) setShowPopup(show_popup);
      
      // Handle chat pause after service completion
      if (chat_paused) {
        setChatPaused(true);
        setPauseMessage(pause_message || "");
        if (all_services_completed) {
          setAllServicesCompleted(true);
        }
      }
      
      // إذا انتهت رحلة الخدمة، أضف الخدمة للمكتملة
      if (service_completed && service_completed.service_id) {
        setCompletedServices(prev => [...new Set([...prev, service_completed.service_id])]);
        setInProgressServices(prev => prev.filter(id => id !== service_completed.service_id));
        setActiveServiceId(null);
      } else if (service_summary && service_summary.service_id) {
        setCompletedServices(prev => [...new Set([...prev, service_summary.service_id])]);
        setInProgressServices(prev => prev.filter(id => id !== service_summary.service_id));
        setActiveServiceId(null);
      }
      
      // Update plan services from dashboard_data
      if (dashboard_data?.services) {
        setPlanServices(dashboard_data.services);
      }
    } catch (e) { addMsg("ai", "عذراً، حدث خطأ. حاول مرة أخرى."); }
    finally { setIsTyping(false); }
  };

  const handleShowPlan = async () => {
    setShowPopup(null);
    if (!sessionId) return;
    setIsTyping(true);
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/show-plan`);
      const { message, ai_message, quick_replies, current_state, dashboard_data, chat_paused, pause_message } = res.data;
      
      // Store plan data and enter Plan Mode
      if (dashboard_data?.services) {
        setPlanServices(dashboard_data.services);
        setDiagnosisSummary(dashboard_data.diagnosis_summary || dashboard_data.diagnosis || {});
        setIsPlanMode(true);
        
        // Add AI message first
        if (message || ai_message) addMsg("ai", message || ai_message);
        
        // Then add the interactive plan card as a chat message
        addPlanCardMessage(dashboard_data.services, dashboard_data.diagnosis_summary || dashboard_data.diagnosis || {});
      } else {
        if (message || ai_message) addMsg("ai", message || ai_message);
      }
      
      // Pause chat when plan is shown
      if (chat_paused) {
        setChatPaused(true);
        setPauseMessage(pause_message || "اختر خدمة من الخطة أعلاه للبدء.");
      }
      
      setQuickReplies([]);
      setCurrentState(current_state); 
      setDashboardData(dashboard_data);
    } catch (e) { console.error(e); }
    finally { setIsTyping(false); }
  };

  const handleStartService = async (serviceId) => {
    if (!sessionId || !serviceId) return;
    
    // Mark service as in progress and set as active
    setActiveServiceId(serviceId);
    setInProgressServices(prev => [...new Set([...prev, serviceId])]);
    setChatPaused(false); // Resume chat when service is started
    setPauseMessage("");
    setIsTyping(true);
    
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/start-service`, { service_id: serviceId });
      const { message, ai_message, quick_replies, current_state, dashboard_data, consultants: newConsultants } = res.data;
      if (message || ai_message) addMsg("ai", message || ai_message);
      setQuickReplies(quick_replies || []); setCurrentState(current_state);
      if (dashboard_data !== undefined) setDashboardData(dashboard_data);
      if (newConsultants) setConsultants(newConsultants);
    } catch (e) { addMsg("ai", "عذراً، حدث خطأ. حاول مرة أخرى."); }
    finally { setIsTyping(false); }
  };

  const handleConsultantSelect = (consultant) => { handleSend(`أختار المستشار: ${consultant.name}`); setConsultants([]); };
  const handleNewChat = () => { createSession(); };
  const isWelcome = currentState === "WELCOME" && messages.length === 0;

  // زر العودة للخطة - show when plan exists but not in plan mode yet
  const canShowPlanButton = planServices.length > 0 && !isPlanMode && currentState !== "WELCOME";

  // Guided Tour - manual only, no auto-start
  const { startTour, isTourCompleted, closeTour } = useGuidedTour();
  
  // Auto-start tour ONLY on first visit AND in welcome state
  useEffect(() => {
    if (isWelcome && !isTourCompleted()) {
      const timer = setTimeout(() => {
        startTour();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isWelcome, isTourCompleted, startTour]);

  // Close tour when leaving welcome state
  useEffect(() => {
    if (!isWelcome) {
      closeTour();
    }
  }, [isWelcome, closeTour]);

  return (
    <div style={{ height: '100vh', width: '100%', display: 'flex', overflow: 'hidden', backgroundColor: COLORS.bgGray, fontFamily: FONT_FAMILY }}>
      {/* Popups */}
      {showPopup && showPopup.type === "plan_ready" && <PlanReadyPopup title={showPopup.title} subtitle={showPopup.subtitle} buttonText={showPopup.button_text} onShowPlan={handleShowPlan} />}

      {/* Chat Area - Left Side */}
      <div data-tour="chat-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', minWidth: 0 }} dir="rtl">
        {/* Header */}
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${COLORS.border}`, backgroundColor: 'white', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <HalaAvatar state={avatarState} size={48} showStatusDot={true} />
            <div>
              <h1 style={{ fontSize: '14px', fontWeight: 'bold', color: COLORS.secondary, margin: 0, fontFamily: FONT_FAMILY }}>هلا</h1>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: 0, fontFamily: FONT_FAMILY }}>
                {avatarState === AVATAR_STATES.THINKING ? 'جاري التفكير...' : 
                 avatarState === AVATAR_STATES.LISTENING ? 'أستمع إليك...' :
                 avatarState === AVATAR_STATES.SPEAKING ? 'أتحدث الآن...' :
                 avatarState === AVATAR_STATES.SUCCESS ? 'وجدت لك شيئاً!' :
                 'مساعدة هلا'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={handleNewChat} data-testid="new-chat-btn" style={{ fontSize: '12px', color: COLORS.textMuted, border: `1px solid ${COLORS.border}`, padding: '8px 16px', borderRadius: '9999px', backgroundColor: 'white', cursor: 'pointer', fontFamily: FONT_FAMILY }}>محادثة جديدة</button>
          </div>
        </header>

        {/* Messages Area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }} className="chat-scroll">
          {isWelcome && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', padding: '0 16px' }}>
              <HalaAvatar state={AVATAR_STATES.IDLE} size={160} showStatusDot={false} style={{ marginBottom: '20px' }} />
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.secondary, marginBottom: '12px', fontFamily: FONT_FAMILY }}>أهلاً! أنا هلا</h2>
              <p style={{ color: COLORS.textMuted, fontSize: '14px', marginBottom: '32px', maxWidth: '350px', lineHeight: 1.6, fontFamily: FONT_FAMILY }}>أساعدك تكتشف الخدمات المناسبة لتطوير مشروعك</p>
              <button data-testid="start-discovery-btn" data-tour="start-btn" onClick={handleStartDiscovery} style={{ padding: '16px 40px', backgroundColor: COLORS.primary, color: 'white', borderRadius: '12px', fontWeight: 'bold', fontSize: '16px', border: 'none', cursor: 'pointer', boxShadow: '0 10px 15px -3px rgba(29, 143, 78, 0.2)', fontFamily: FONT_FAMILY }}>ابدأ الآن</button>
              
              {/* زر راجع خطتك */}
              <button 
                data-testid="review-plan-btn"
                onClick={() => window.location.href = '/review-plan'}
                style={{ 
                  marginTop: '16px',
                  padding: '12px 32px', 
                  backgroundColor: 'transparent', 
                  color: COLORS.secondary, 
                  borderRadius: '12px', 
                  fontWeight: '600', 
                  fontSize: '14px', 
                  border: `1px solid ${COLORS.border}`, 
                  cursor: 'pointer', 
                  fontFamily: FONT_FAMILY,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FileText style={{ width: '16px', height: '16px' }} />
                راجع خطتك السابقة
              </button>

              {/* Financial Advisor Entry */}
              <button 
                data-testid="financial-advisor-btn"
                onClick={() => window.location.href = '/financial-advisor'}
                style={{ 
                  marginTop: '12px',
                  padding: '14px 32px', 
                  background: `linear-gradient(135deg, ${COLORS.secondary}, #2A5298)`,
                  color: 'white', 
                  borderRadius: '12px', 
                  fontWeight: 'bold', 
                  fontSize: '14px', 
                  border: 'none', 
                  cursor: 'pointer', 
                  fontFamily: FONT_FAMILY,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 12px rgba(27, 58, 107, 0.25)',
                }}
              >
                <BarChart3 style={{ width: '18px', height: '18px' }} />
                هلا مستشارتك المالية
              </button>
            </div>
          )}

          {messages.map((msg, index) => (
            <div key={msg.id} style={{ marginBottom: '20px' }} className="message-enter">
              {msg.sender === "ai" ? (
                // رسالة هلا
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <HalaAvatar 
                    state={index === messages.length - 1 && !isTyping ? AVATAR_STATES.SPEAKING : AVATAR_STATES.IDLE} 
                    size={40} 
                    showStatusDot={false} 
                  />
                  <div style={{ flex: 1, maxWidth: msg.plan_card ? '95%' : '80%' }}>
                    {/* Text bubble - only if there's text */}
                    {msg.text && (
                      <div style={{ backgroundColor: COLORS.halaBubble, padding: '12px 16px', borderRadius: '16px', borderTopRightRadius: '4px' }}>
                        <p style={{ fontSize: '14px', color: 'white', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6, fontFamily: FONT_FAMILY }}>{msg.text}</p>
                      </div>
                    )}
                    {/* Inline Plan Card */}
                    {msg.plan_card && (
                      <InlinePlanCard
                        planData={msg.plan_card}
                        onStartService={handleStartService}
                        completedServices={completedServices}
                        inProgressServices={inProgressServices}
                        activeServiceId={activeServiceId}
                      />
                    )}
                    <span style={{ fontSize: '10px', color: COLORS.textLight, marginTop: '4px', display: 'block', fontFamily: FONT_FAMILY }}>{msg.time}</span>
                    {msg.service_summary && <ServiceSummaryCard summary={msg.service_summary} />}
                  </div>
                </div>
              ) : (
                // رسالة المستخدم
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <div style={{ maxWidth: '80%' }}>
                    <div style={{ backgroundColor: COLORS.bgGray, color: COLORS.text, padding: '12px 16px', borderRadius: '16px', borderTopLeftRadius: '4px', border: `1px solid ${COLORS.border}` }}>
                      <p style={{ fontSize: '14px', margin: 0, lineHeight: 1.6, fontFamily: FONT_FAMILY }}>{msg.text}</p>
                    </div>
                    <span style={{ fontSize: '10px', color: COLORS.textLight, marginTop: '4px', display: 'block', textAlign: 'left', fontFamily: FONT_FAMILY }}>{msg.time}</span>
                  </div>
                </div>
              )}
            </div>
          ))}

          {consultants.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, marginBottom: '12px', fontWeight: 500, fontFamily: FONT_FAMILY }}>اختر المستشار المناسب:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {consultants.map((c, index) => <ConsultantCard key={c.consultant_id} consultant={c} onSelect={handleConsultantSelect} isRecommended={index === 0} />)}
              </div>
            </div>
          )}

          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {quickReplies.length > 0 && !chatPaused && <div data-tour="quick-replies" style={{ padding: '12px 20px', borderTop: `1px solid ${COLORS.border}`, backgroundColor: 'rgba(249, 250, 251, 0.5)', flexShrink: 0 }}><QuickReplies replies={quickReplies} onSelect={handleSend} /></div>}

        {/* Chat Paused Banner */}
        {chatPaused && !isWelcome && (
          <div style={{ 
            padding: '16px 20px', 
            borderTop: `1px solid ${COLORS.border}`, 
            backgroundColor: allServicesCompleted ? COLORS.primaryLight : '#FEF3C7',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: allServicesCompleted ? COLORS.primary : COLORS.gold,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              {allServicesCompleted ? (
                <Check style={{ width: '20px', height: '20px', color: 'white' }} />
              ) : (
                <Rocket style={{ width: '20px', height: '20px', color: 'white' }} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ 
                fontSize: '14px', 
                fontWeight: 'bold', 
                color: allServicesCompleted ? COLORS.primaryDark : '#92400E', 
                margin: 0, 
                fontFamily: FONT_FAMILY 
              }}>
                {allServicesCompleted ? 'أكملت جميع خدمات خطتك!' : 'خطتك جاهزة'}
              </p>
              <p style={{ 
                fontSize: '12px', 
                color: allServicesCompleted ? COLORS.textMuted : '#B45309', 
                margin: '2px 0 0 0', 
                fontFamily: FONT_FAMILY 
              }}>
                {pauseMessage || (allServicesCompleted ? 'سيتم التواصل معك قريباً.' : 'اختر خدمة من الخطة للبدء')}
              </p>
            </div>
            {!allServicesCompleted && !showDashboard && (
              <button
                onClick={() => setShowDashboard(true)}
                style={{
                  padding: '10px 16px',
                  backgroundColor: COLORS.gold,
                  color: 'white',
                  borderRadius: '10px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontFamily: FONT_FAMILY,
                }}
              >
                عرض الخطة
              </button>
            )}
          </div>
        )}

        {!isWelcome && !chatPaused && (
          <div style={{ padding: '16px 20px', borderTop: `1px solid ${COLORS.border}`, backgroundColor: 'white', flexShrink: 0 }}>
            <form onSubmit={(e) => { e.preventDefault(); handleSend(inputText); }} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input type="text" value={inputText} onChange={(e) => setInputText(e.target.value)} placeholder="اكتب رسالتك هنا..." data-testid="chat-input" style={{ flex: 1, padding: '12px 16px', backgroundColor: COLORS.bgGray, border: `1px solid ${COLORS.border}`, borderRadius: '12px', fontSize: '14px', outline: 'none', fontFamily: FONT_FAMILY }} />
              <button type="submit" disabled={!inputText.trim()} data-testid="send-btn" style={{ width: '44px', height: '44px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: inputText.trim() ? COLORS.primary : COLORS.bgGray, border: 'none', cursor: inputText.trim() ? 'pointer' : 'default' }}>
                <Send style={{ width: '20px', height: '20px', color: inputText.trim() ? 'white' : COLORS.textLight, transform: 'rotate(180deg)' }} />
              </button>
            </form>
          </div>
        )}

        <div style={{ padding: '8px 0', textAlign: 'center', borderTop: `1px solid ${COLORS.border}`, backgroundColor: 'rgba(249, 250, 251, 0.3)', flexShrink: 0 }}><p style={{ fontSize: '11px', color: COLORS.textLight, margin: 0, fontFamily: FONT_FAMILY }}>هلا — مساعدتك الذكية</p></div>
      </div>

      {/* Dashboard - Right Side */}
      {showDashboard && (
        <div data-tour="dashboard" style={{ width: '380px', flexShrink: 0, borderRight: `1px solid ${COLORS.border}`, height: '100%', overflow: 'hidden', backgroundColor: COLORS.bgGray }} dir="rtl">
          <DynamicDashboard 
            currentState={currentState} 
            dashboardData={dashboardData} 
            onStartService={handleStartService} 
            onStartDiscovery={handleStartDiscovery}
            showPlanButton={canShowPlanButton}
            onShowPlan={handleShowPlan}
            planServicesCount={planServices.length}
            onStartTour={startTour}
            // Plan Journey Mode props
            isPlanMode={isPlanMode}
            planServices={planServices}
            planDiagnosis={diagnosisSummary}
            activeServiceId={activeServiceId}
            completedServices={completedServices}
            inProgressServices={inProgressServices}
          />
        </div>
      )}
    </div>
  );
}
